// Does the house already have this? Pure.
//
// The join is by NAME, exactly the way Meal Hat's fridge does it (lowercased
// and trimmed, nothing fuzzier), with one addition: a lesson ingredient can
// carry aliases, because a recipe says "parmesan" where the catalog says
// "Parmesan Cheese" and the fridge says "Parmesean". Every alias is tried
// against both the fridge's live timers and the catalog.
//
// Direction rule, inherited: the fridge may only ever say YOU HAVE IT. No
// timer means nobody has described that food, not that you are out of it.
// An expired timer does not count.

export const normalizeName = (name) => String(name || '').trim().toLowerCase();

const namesFor = (ingredient) => {
  const names = [ingredient?.name, ...(ingredient?.aliases || [])].map(normalizeName).filter(Boolean);
  return Array.from(new Set(names));
};

const isLive = (timer, now) => {
  const at = new Date(timer?.expiryDate).getTime();
  return !Number.isNaN(at) && at > now.getTime();
};

/** Live fridge timers that name this ingredient or one of its aliases. */
export function timersFor (ingredient, timers = {}, now = new Date()) {
  const wanted = new Set(namesFor(ingredient));
  return Object.entries(timers || {})
    .map(([id, timer]) => ({ id, ...timer }))
    .filter((timer) => timer && wanted.has(normalizeName(timer.title)) && isLive(timer, now))
    .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));
}

/** The catalog entry this ingredient maps to, if Meal Hat already knows it. */
export function catalogEntryFor (ingredient, catalog = {}) {
  const wanted = namesFor(ingredient);
  const entries = Object.values(catalog || {});
  for (const name of wanted) {
    const hit = entries.find((entry) => normalizeName(entry?.name) === name);
    if (hit) return hit;
  }
  return null;
}

/** An unpurchased shopping-list row for this grocery id, if there is one. */
export function shoppingRowFor (groceryId, shoppingList = {}) {
  if (!groceryId) return null;
  return Object.values(shoppingList || {}).find((row) => row?.groceryId === groceryId && !row.purchased) || null;
}

/**
 * One verdict per grocery ingredient:
 *   'have'    — a live timer says it is in the house
 *   'listed'  — already on the Meal Hat shopping list, unpurchased
 *   'pantry'  — a staple we assume is there (nothing says otherwise); shown, not bought
 *   'need'    — goes on the list
 */
export function assessIngredients (lesson, { timers = {}, catalog = {}, shoppingList = {} } = {}, now = new Date()) {
  return (lesson?.grocery || []).map((ingredient) => {
    const have = timersFor(ingredient, timers, now);
    const entry = catalogEntryFor(ingredient, catalog);
    const row = shoppingRowFor(entry?.id, shoppingList);
    let status = 'need';
    if (have.length) status = 'have';
    else if (row) status = 'listed';
    else if (ingredient.pantry) status = 'pantry';
    return {
      ...ingredient,
      status,
      timers: have,
      catalogEntry: entry,
      onList: Boolean(row),
      // What the fridge calls it, when it differs — so the screen can say
      // "Parmesean, 6 weeks left" rather than pretending the names match.
      fridgeName: have[0]?.title || null
    };
  });
}

/** Days until the soonest of this ingredient's timers goes off. */
export function daysLeft (timer, now = new Date()) {
  const at = new Date(timer?.expiryDate).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.round((at - now.getTime()) / 86400000));
}
